<template>
  <div class="person-list">
    <p v-if="!value.length" class="text-muted">
      No {{ config.plural.toLowerCase() }} added yet.
      <template v-if="!disabled">Use the button below to add one.</template>
    </p>

    <b-card
      v-for="(person, index) in value"
      :key="personKey(person, index)"
      no-body
      class="person-card mb-1"
    >
      <div class="person-card-head">
        <span class="person-index">{{ index + 1 }}</span>
        <div class="flex-grow-1">
          <strong>{{ config.singular }} {{ index + 1 }}</strong>
          <small v-if="person.name" class="text-muted d-block">{{ person.name }}</small>
        </div>
        <b-button v-if="!disabled" variant="flat-danger" size="sm" @click="removePerson(index)">
          <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
          Remove
        </b-button>
      </div>

      <b-card-body>
        <fieldset :disabled="disabled">
          <b-row>
            <template v-for="item in fieldItems">
              <!-- the person's name: required, drives the label and the autosave -->
              <b-col v-if="item.builtinKey === 'personName'" :key="item.key" cols="12" :md="item.cols || 6">
                <b-form-group :label="item.label + ' *'">
                  <validation-provider #default="{ errors }" rules="required" :name="item.label + ' (' + config.singular + ' ' + (index + 1) + ')'">
                    <b-form-input v-model="person.name" :state="errors.length ? false : null" @blur="nameLeft(person)" />
                    <small v-if="item.help" class="text-muted d-block">{{ item.help }}</small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col v-else-if="item.inputType === 'heading'" :key="item.key" cols="12">
                <question-field :item="item" :form="form" :person="person" />
              </b-col>

              <b-col v-else :key="item.key" cols="12" :md="item.cols || 6">
                <question-field
                  :item="item"
                  :value="person[item.dataKey]"
                  :form="form"
                  :person="person"
                  :disabled="disabled"
                  :name="item.label + ' (' + config.singular + ' ' + (index + 1) + ')'"
                  @input="setField(person, item.dataKey, $event)"
                />
              </b-col>
            </template>
          </b-row>
        </fieldset>

        <!-- documents for this person (stay available after signing) -->
        <div v-if="!preview || requirementsFor(person).length" class="person-docs mt-1">
          <h6 class="mt-0">Documents for this {{ config.singular.toLowerCase() }}</h6>
          <requirement-upload-list
            v-if="person.personId || preview"
            :requirements="requirementsFor(person)"
            :can-upload="canUpload && !preview"
            empty-text="No documents are required for this person."
            @payload="$emit('payload', $event)"
          />
          <small v-else class="text-muted">
            <feather-icon icon="InfoIcon" size="12" class="mr-25" />
            Documents for this person can be uploaded once their details have been saved
            (enter the name and continue, or use "Save progress").
          </small>
        </div>

        <b-form-group v-if="signatureItem && !disabled" :label="signatureItem.label" class="mt-1 mb-0">
          <signature-pad
            :value="person.signatureImage || null"
            :active="active"
            height="160px"
            @input="setSignature(person, $event)"
          />
          <small class="text-muted">{{ signatureItem.help || "The signature is submitted together with the form when it is signed." }}</small>
        </b-form-group>
      </b-card-body>
    </b-card>

    <b-button v-if="!disabled" variant="flat-primary" @click="addPerson">
      <feather-icon icon="PlusIcon" size="14" class="mr-25" />
      Add {{ config.singular.toLowerCase() }}
    </b-button>
  </div>
</template>

<script>
// Generic person list (traders, signatories, directors, UBOs, officers, sub-funds).
// Fields come from the schema section's items; `value` is the array stored under
// form.persons[section.formKey], edited in place, add/remove emit `input` with a new
// array. `request-save` asks the shell to autosave so new persons receive a personId.
import { ValidationProvider } from "vee-validate";
import { required } from "@validations";
import RequirementUploadList from "./RequirementUploadList.vue";
import SignaturePad from "./SignaturePad.vue";
import QuestionField from "./QuestionField.vue";
import { emptyPerson } from "./formSchema";

export default {
  name: "PersonListSection",

  components: { ValidationProvider, RequirementUploadList, SignaturePad, QuestionField },

  props: {
    config: { type: Object, required: true },
    section: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    form: { type: Object, default: () => ({}) },
    requirements: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    canUpload: { type: Boolean, default: true },
    active: { type: Boolean, default: true },
    preview: { type: Boolean, default: false },
  },

  data() {
    return { required };
  },

  computed: {
    fieldItems() {
      return (this.section.items || []).filter((item) => item.builtinKey !== "personSignature");
    },
    signatureItem() {
      return (this.section.items || []).find((item) => item.builtinKey === "personSignature") || null;
    },
  },

  methods: {
    personKey(person, index) {
      return person.personId ? "p" + person.personId : "new" + index;
    },

    requirementsFor(person) {
      return this.requirements.filter(
        (row) => row.Scope === this.config.personType && Number(row.PersonId) === Number(person.personId)
      );
    },

    setField(person, key, value) {
      this.$set(person, key, value);
    },

    addPerson() {
      this.$emit("input", [...this.value, emptyPerson(this.section)]);
    },

    removePerson(index) {
      this.$emit(
        "input",
        this.value.filter((_, i) => i !== index)
      );
    },

    // A freshly named person has no personId yet: ask the shell to save so the id
    // (and therefore the person's document rows) exist.
    nameLeft(person) {
      if (!this.preview && !person.personId && person.name && person.name.trim()) {
        this.$emit("request-save");
      }
    },

    setSignature(person, dataUrl) {
      this.$set(person, "signatureImage", dataUrl || null);
    },

    // base64 PNG per person, aligned with `value` (null when not drawn)
    collectSignatures() {
      return this.value.map((person) => person.signatureImage || null);
    },
  },
};
</script>
